import type {
  AudioDeviceState,
  AudioInputDevice,
  EvaluationInput,
  EvaluationResult,
} from './audioDeviceTypes';
import { findStoredDevice } from './findStoredDevice';
import { matchesRecommendedDevice } from './recommendedDevice';

/**
 * Blocked access comes first because without access the device list carries no labels. With a known
 * recommended device and an empty list, "recommended missing" wins over "no device": its
 * troubleshooting steps are the more helpful ones.
 */
export function evaluateAudioDevices(input: EvaluationInput): EvaluationResult {
  const { devices, settings, probe, micAccessStatus } = input;

  const recommendedPresent = devices.filter((device) => matchesRecommendedDevice(device.label));
  const recommendedCandidate =
    findStoredDevice(recommendedPresent, settings.recommendedMicrophone) ??
    recommendedPresent[0] ??
    null;
  const confirmed = findStoredDevice(devices, settings.confirmedMicrophone) ?? null;

  const withoutDevice = (state: AudioDeviceState): EvaluationResult => ({
    state,
    selectedDevice: null,
    recommendedCandidate,
    recommendedAvailable: false,
  });
  const probeFailed = probe !== null && !probe.ok;

  if (
    micAccessStatus === 'denied' ||
    micAccessStatus === 'restricted' ||
    (probeFailed && probe.error === 'not-allowed')
  ) {
    return withoutDevice('accessBlocked');
  }

  if (devices.length === 0) {
    return withoutDevice(settings.recommendedMicrophone ? 'recommendedMissing' : 'noDevice');
  }

  if (confirmed) {
    if (probeFailed) {
      return withoutDevice(
        probe.error === 'not-readable' ? 'confirmedInaccessible' : 'confirmedMissing'
      );
    }
    return connected('confirmedConnected', confirmed, recommendedCandidate, true);
  }

  if (recommendedCandidate) {
    if (probeFailed) {
      return withoutDevice(
        probe.error === 'not-readable' ? 'recommendedInaccessible' : 'recommendedMissing'
      );
    }
    return connected('recommendedConnected', recommendedCandidate, recommendedCandidate, false);
  }

  if (settings.confirmedMicrophone) return withoutDevice('confirmedMissing');
  if (settings.recommendedMicrophone) return withoutDevice('recommendedMissing');
  return withoutDevice('recommendedNeverSeen');
}

function connected(
  state: AudioDeviceState,
  selectedDevice: AudioInputDevice,
  recommendedCandidate: AudioInputDevice | null,
  flagRecommended: boolean
): EvaluationResult {
  return {
    state,
    selectedDevice,
    recommendedCandidate,
    recommendedAvailable: flagRecommended && recommendedCandidate !== null,
  };
}
